import { Grid, Typography, Button, TextField, Skeleton } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import React, { useEffect, useState } from "react";
import useProjects from "../../hooks/useProjects";

export default function VerificationUrlSection({ projectDetails, projectId }) {
  const {
    updateVerificationUrl,
    verificationUrl,
    updatingVerificationUrl,
    isVeirifcationUrlSaved,
    removeVerificationUrl,
    removingVerificationUrl,
    isVeirifcationUrlRemoved,
  } = useProjects();

  const [isEditing, setIsEditing] = useState(false);
  const [localUrl, setLocalUrl] = useState("");
  const [isProjectLoading, setIsProjectLoading] = useState(true);
  const [hasExistingUrl, setHasExistingUrl] = useState(false);

  // ✅ Load initial data
  useEffect(() => {
    if (projectDetails) {
      setIsProjectLoading(false);
      setHasExistingUrl(!!projectDetails.verification_url);
      setLocalUrl(projectDetails.verification_url || "");
    }
  }, [projectDetails]);

  // ✅ Update UI after successful save
  useEffect(() => {
    if (isVeirifcationUrlSaved && verificationUrl) {
      setLocalUrl(verificationUrl);
      setIsEditing(false);
      setHasExistingUrl(true);
    }
  }, [isVeirifcationUrlSaved, verificationUrl]);

  // ✅ Update UI after successful remove
  useEffect(() => {
    if (isVeirifcationUrlRemoved) {
      setLocalUrl("");
      setIsEditing(false);
      setHasExistingUrl(false);
    }
  }, [isVeirifcationUrlRemoved]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setLocalUrl(projectDetails?.verification_url || verificationUrl || "");
  };

  const handleSaveClick = async () => {
    if (!localUrl.trim()) return;
    try {
      const formData = new FormData();
      formData.append("project_id", projectId);
      formData.append("verification_url", localUrl.trim());
      await updateVerificationUrl(formData);
    } catch (err) {
      console.error("Error saving verification URL", err);
    }
  };

  const handleRemoveClick = async () => {
    try {
      const formData = new FormData();
      formData.append("project_id", projectId);
      formData.append("verification_url", "");
      await removeVerificationUrl(formData);
    } catch (err) {
      console.error("Error removing verification URL", err);
    }
  };

  if (isProjectLoading) {
    return (
      <Grid container sx={{ ml: 2, mt: "16px" }}>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={70} width="100%" />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ ml: 2, mt: "16px" }}>
      <Grid
        container
        xs={12}
        sm={12}
        md={12}
        lg={12}
        xl={12}
        sx={{ alignSelf: "center", display: "flex", mt: 1 }}
      >
        <Grid
          item
          xs={6}
          sm={6}
          md={4}
          lg={4}
          xl={4}
          sx={{ alignSelf: "center" }}
        >
          <Typography
            sx={{ fontWeight: "bold", alignSelf: "center", fontSize: 14 }}
          >
            Verification URL
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          {/* --- CASE 1: No URL yet (input + save only) --- */}
          {!hasExistingUrl && !isEditing && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <TextField
                  size="small"
                  placeholder="Enter verification URL"
                  fullWidth
                  value={localUrl}
                  onChange={(e) => setLocalUrl(e.target.value)}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveClick}
                  disabled={!localUrl.trim() || updatingVerificationUrl}
                  startIcon={
                    updatingVerificationUrl && (
                      <ThreeDots height="20" width="20" radius="9" color="#fff" />
                    )
                  }
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  {updatingVerificationUrl ? "Saving" : "Save"}
                </Button>
              </Grid>
            </Grid>
          )}

          {/* --- CASE 2: URL already saved (view/edit/remove mode) --- */}
          {hasExistingUrl && !isEditing && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <Typography sx={{ fontSize: 14, wordBreak: "break-word" }}>
                  {localUrl}
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditClick}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Edit
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={handleRemoveClick}
                  disabled={removingVerificationUrl}
                  startIcon={
                    removingVerificationUrl && (
                      <ThreeDots height="20" width="20" radius="9" color="#fff" />
                    )
                  }
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  {removingVerificationUrl ? "Removing" : "Remove"}
                </Button>
              </Grid>
            </Grid>
          )}

          {/* --- CASE 3: Editing existing URL (text + save + cancel) --- */}
          {isEditing && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <TextField
                  size="small"
                  fullWidth
                  value={localUrl}
                  onChange={(e) => setLocalUrl(e.target.value)}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveClick}
                  disabled={!localUrl.trim() || updatingVerificationUrl}
                  startIcon={
                    updatingVerificationUrl && (
                      <ThreeDots height="20" width="20" radius="9" color="#fff" />
                    )
                  }
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  {updatingVerificationUrl ? "Saving" : "Save"}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={handleCancelClick}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
